import { useState } from "react";
import { toast } from "react-toastify";
import { useApiClient } from "@/hooks/useApiClient";
import { type ReuseInfoGetAPIResponse } from "@/features/acquireBook/api/ReuseInfoAPI";
import { getAcquireReuse } from "@/features/propertyCodeAcquisition/api/getAcquireReuse";
import { handlePropertyCodeApiError } from "@/features/propertyCodeAcquisition/utils/handlePropertyCodeApiError";

// 物件コード専用のリユース確認 hook。
//
// 所在/地番を付けず物件コード配下エンドポイントを叩き、結果（ReuseInfoGetAPIResponse[]）を
// 状態として保持する。状態のインターフェース（reuseInfoGetMutateAPIResponseList /
// setReuseInfoGetMutateAPIResponseList）は usePictureCheckOptionsManager の props にそのまま渡せる形にする。

/** リユース可能な登記/図面が無かった場合のメッセージ。 */
export const NO_REUSE_AVAILABLE_MESSAGE =
  "リユース可能な登記・図面はありませんでした";

/** リユース確認失敗時の汎用メッセージ（5xx・非 Axios 等）。 */
export const REUSE_CHECK_FAILED_MESSAGE =
  "リユース確認に失敗しました。時間をおいて再度お試しください。";

/**
 * リユース確認の実行結果。
 * 失敗（例外）と「成功したが 0 件」を呼び出し側で区別できるようにするための判別ユニオン。
 * 失敗時に 0 件成功と同一視してフォームを初期化してしまうのを防ぐ。
 */
export interface AcquireReuseResult {
  /** リユース確認 API が成功したか。0 件成功は ok=true・data=[]、失敗は ok=false。 */
  ok: boolean;
  /** 成功時のリユース可能一覧（失敗時は []）。 */
  data: ReuseInfoGetAPIResponse[];
}

export interface UseAcquireReuseReturn {
  /** リユース確認 API を呼び、結果を状態へ反映する。成功/失敗を区別した結果を返す。 */
  acquireReuse: (cancelLoading: () => void) => Promise<AcquireReuseResult>;
  /** リユース確認 API のレスポンス（インライン反映の元データ）。 */
  reuseInfoGetMutateAPIResponseList: ReuseInfoGetAPIResponse[];
  /** レスポンスの差し替え（usePictureCheckOptionsManager から初期化に使われる）。 */
  setReuseInfoGetMutateAPIResponseList: (
    value: ReuseInfoGetAPIResponse[]
  ) => void;
}

/**
 * 物件コード専用のリユース確認 hook。
 * @param propertyCode 物件コード（UUID）
 */
export const useAcquireReuse = (
  propertyCode: string
): UseAcquireReuseReturn => {
  const { apiClient } = useApiClient();
  const [
    reuseInfoGetMutateAPIResponseList,
    setReuseInfoGetMutateAPIResponseList,
  ] = useState<ReuseInfoGetAPIResponse[]>([]);

  const acquireReuse = async (
    cancelLoading: () => void
  ): Promise<AcquireReuseResult> => {
    try {
      const data = await getAcquireReuse(apiClient, propertyCode);
      setReuseInfoGetMutateAPIResponseList(data);
      if (data.length === 0) {
        toast.info(NO_REUSE_AVAILABLE_MESSAGE);
      }
      return { ok: true, data };
    } catch (e) {
      // 失敗時はリユース情報を初期化し、新規取得のみ可能な状態に戻す（履歴整合性を壊さない）。
      setReuseInfoGetMutateAPIResponseList([]);
      // 403→閲覧専用 / 5xx・非 Axios→Sentry+汎用 を共通ハンドラに委譲。
      handlePropertyCodeApiError(e, {
        fallbackMessage: REUSE_CHECK_FAILED_MESSAGE,
      });
      // 失敗時は ok=false で返す（呼び出し側で初期化をスキップ）。
      return { ok: false, data: [] };
    } finally {
      cancelLoading();
    }
  };

  return {
    acquireReuse,
    reuseInfoGetMutateAPIResponseList,
    setReuseInfoGetMutateAPIResponseList,
  };
};
